import {action, autorun, computed, observable, runInAction} from 'mobx';
import React from 'react';
import {FormattedMessage} from 'react-intl';
import range from 'lodash/range';
import first from 'lodash/first';
import last from 'lodash/last';
import isNumber from 'lodash/isNumber';
import isEmpty from 'lodash/isEmpty';
import looperStore from '../looper/store';
import appStore from '../../app/store';
import chatStore from '../../common/chat/store';
import actionStore from '../../../stores/ActionStore';
import {getRangeString} from '../../../utils/commonUtility';
import {translate} from '../../../utils/localizationUtility';
import {actions, AlertType, category, events, TrackActionType} from '../../../constants/enums';
import Player from '../../../stores/PlayerStore';
import {gridDivisionArray, MAX_LOOPER_TRACKS, pitchShiftArray, transpositionArray} from '../../../constants/constants';
import {getColorByTier} from '../../../themes/colors';
import {ceilTwoDecimal} from '../../../utils/audioUtility';
import TrackAction from '../../../stores/TrackActionStore';
import {getBeatFromAngle} from './studioUtility';
import {customEvent} from '../../../utils/analyticsUtil';
import {isGuest} from "../../../utils/userUtility";

const MAX_NUM_BEATS = 40;

class StudioStore extends Player {
    @observable isEditMode = false;
    @observable playSlice = {start: null, end: null};
    @observable bpm = 120;
    @observable pitchLabel = '0';
    @observable studioTracks = this.initStudioTracks();
    // @observable leaderTrack = this.initTrack({});
    @observable clipboard = this.initClipboard();

    @observable numBeatsPerBar = 4;
    @observable timeSignatureMeter = 4;
    @observable timeSignatureLabel = '4/4';
    @observable gridDivision = 1;
    @observable gridDivisionLabel = '1/4';
    @observable isShowWaves = true;

    @observable transposeArray = pitchShiftArray.slice();

    @observable isLoopActive = false;

    userGridDivision = null;


    constructor() {
        super();
        autorun(() => {
            this.setBpm(looperStore.bpm || 120);
        });

        autorun(() => {
            this.getStudioTrackDataFromLoopers(looperStore.looperTracks, looperStore.isLooperLoaded);
        });

        autorun(() => {
            const pitchItem = this.transposeArray.find(item => item.value === looperStore.pitch) || {label: '(G)'};
            this.setPitch(looperStore.pitch || 0, pitchItem.label);
        });

        autorun(() => {
            if (looperStore.playSlice) {
                runInAction(() => {
                    if (isNumber(looperStore.playSlice.start)) {
                        this.playSlice.start = looperStore.playSlice.start * this.gridDivision;
                        this.playSlice.end = looperStore.playSlice.end * this.gridDivision;
                    } else {
                        this.playSlice = {start: null, end: null};
                    }

                    // console.log('studio ' + JSON.stringify(this.playSlice));
                });
            }
        })

    }

    /**
     * initialize the copy data
     * @returns {{nextPasteIndex: number, copiedBars: Map<any, any>}}
     */
    initClipboard = () => {
        return {nextPasteIndex: 0, copiedBars: new Map(), copyOffset: new Map(), isCut: false};
    };


    @action setTimeSignature = (numBeatsPerBar, timeSignatureMeter, timeSignatureLabel) => {
        this.handleAddNewAction({
            id: 'global',
            bpm: this.bpm,
            tracks: this.looperTracks
        }, TrackActionType.ChangeTSMeter, [], [numBeatsPerBar, timeSignatureMeter, timeSignatureLabel], null, true);
        // this._updateChangeTSCount(val);
        this.getStudioTrackDataFromLoopers(looperStore.looperTracks, true);

        setTimeout(async () => {
            await looperStore.updateMetronome();
            looperStore.playPauseIfWasPlaying();
        }, 0);
    };

    /**
     * Apply all the track actions to bars and audio
     * @param tracks    {Array} Tracks to apply actions to
     */
    applyTrackActionData = (tracks) => {
        const localTracks = tracks.slice();

        actionStore.getAll().forEach((action) => {
            const track = localTracks.find(track => track.id === action.trackId);
            if (!track && action.trackId !== 'global') {
                console.warn('Action contains a track which does not exists in the project: ', action.trackId);
                return;
            }
            action.gridDivision = action.gridDivision || 1;
            const gridDivision = this.gridDivision / (isNumber(action.gridDivision) ? action.gridDivision : 1);
            switch (action.type) {
                case TrackActionType.Mute:
                case TrackActionType.UnMute:
                    break;
                case TrackActionType.StartOffset:
                    track.splits[action.splitIndex].offset = first(action.target) * gridDivision;
                    break;
                case TrackActionType.Highlight:
                    break;
                case TrackActionType.Copy:
                    const target = action.target.slice().map(t => t * gridDivision);
                    this.clipboard.copiedBars.set(track.id, target);
                    this.clipboard.copyOffset.set(track.id, track.splits[action.splitIndex].offset * gridDivision);
                    break;
                case TrackActionType.Paste:
                    break;
                case TrackActionType.Cut:
                    this.cutBars(track, action.source);
                    break;
                case TrackActionType.Split:
                    const copiedBars = this.clipboard.copiedBars.get(track.id).slice();
                    const copyOffset = this.clipboard.copyOffset.get(track.id);
                    this._handleSplitAction(track, first(action.target) * gridDivision, gridDivision * (last(action.target) + 1), false, first(copiedBars) - copyOffset);
                    break;
                case TrackActionType.SplitRemove:
                    this._handleSplitAction(track, gridDivision * first(action.target), gridDivision * (last(action.target) + 1), true);
                    break;
                case TrackActionType.LeftTrimOffset:
                    const prevLeftTrim = track.splits[action.splitIndex].leftTrim;
                    track.splits[action.splitIndex].leftTrim = first(action.target) * gridDivision;
                    track.splits[action.splitIndex].sourceStart += track.splits[action.splitIndex].leftTrim - prevLeftTrim;
                    break;
                case TrackActionType.RightTrimOffset:
                    track.splits[action.splitIndex].rightTrim = first(action.target) * gridDivision;
                    break;
                case TrackActionType.Padding:
                    this._updateBeatCount(Math.max(this.minGridSize * action.gridDivision, action.target[0]) * gridDivision);
                    break;
                case TrackActionType.ChangeTSMeter:
                    let newNumBeatsPerBar = action.target[0];
                    let newTimeSignature = action.target[1];
                    let newTimeSignatureLabel = action.target[2];

                    // super weird firefox bug is causing target array to be reversed
                    if (typeof action.target[0] === 'string') {
                        newNumBeatsPerBar = action.target[2];
                        newTimeSignature = action.target[1];
                        newTimeSignatureLabel = action.target[0];
                    }

                    let tsMeter = this.timeSignatureMeter;
                    const ratio = newTimeSignature / tsMeter;
                    localTracks.forEach((track) => {
                        track.splits.forEach((split) => {
                            split.start *= ratio;
                            split.end *= ratio;
                            split.offset *= ratio;
                            split.leftTrim *= ratio;
                            split.rightTrim *= ratio;
                        });
                        tsMeter = newTimeSignature;
                    });
                    looperStore.studioBeatsCount *= ratio;
                    this.timeSignatureMeter = newTimeSignature;
                    this.numBeatsPerBar = newNumBeatsPerBar;
                    this.timeSignatureLabel = newTimeSignatureLabel;
                    break;
                default:
                    break;
            }
        });
    };

    /**
     * Build the studio track
     * @param track {Object}    The track object
     * @param barCount?  {Number} optional - the barcount to infoce on the track
     * @returns {Object}    The built track
     */
    @action
    buildStudioTrack(track) {
        const duration = (track.originalDuration * track.originalBPM) / track.bpm;
        const beatLength = (60 / this.bpm) * (4 / this.timeSignatureMeter);
        const trackBeatCount = track.duration / beatLength;
        track.duration = duration;
        track.beatCount = ceilTwoDecimal(trackBeatCount);
        track.splits.replace([{
            start: 0,
            end: Math.max(1, trackBeatCount),
            sourceStart: 0,
            selectedBeats: [],
            offset: 0,
            rightTrim: 0,
            leftTrim: 0,
            selected: false
        }]);

        track.masterBpm = this.bpm;
        // return Object.assign({}, track);
        return track;
    }

    /**
     * set a new item to selected items
     * @param tracks    {Array} Tracks to set or remove to and from selected items
     */
    buildStudioTracks(tracks) {
        const studioTracks = tracks
            .filter(track => track.bpm !== -1)
            .map(track => this.buildStudioTrack(track));
        for (let i = studioTracks.length; i < MAX_LOOPER_TRACKS; i += 1) {
            studioTracks.push({beatCount: 0, progress: 0, duration: 0, currentTime: 0});
        }
        return studioTracks.reverse();
    }

    getSelectedBeatsFromTrackIndex(trackIndex, splitIndex) {
        const track = this.tracks[trackIndex];
        return this.getSelectedBeatsFromTrack(track, splitIndex);
    }

    getSelectedBeatsFromTrack(track, splitIndex) {
        const split = track.splits[splitIndex];
        if (!isEmpty(split.selectedBeats)) {
            return track.splits[splitIndex].selectedBeats;
        }
        return this.getSplitRange(split);
    }


    formatBeatsToBarBeats = (value) => {
        const activeBar = Math.floor((value - 1) / this.numBeatsPerBar) + 1 || 1;
        const activeBeat = Math.floor((value - 1) % this.numBeatsPerBar) + 1 || 1;

        return activeBar + '.' + activeBeat;
    };

    formatBeatsToBarBeatsWithGrid = (angle) => {
        const beatForth = getBeatFromAngle(angle, this.absBeatCount);
        const beatEighth = getBeatFromAngle(angle, this.absBeatCount * 2);
        const beatSixteenth = getBeatFromAngle(angle, this.absBeatCount * 4);
        const bar = Math.floor(beatForth / this.numBeatsPerBar) + 1 || 1;
        const beat = Math.floor(beatForth % this.numBeatsPerBar) + 1 || 1;
        const eighth = Math.floor(beatEighth % (this.numBeatsPerBar * 2)) + 1 || 1;
        const sixteenth = Math.floor(beatSixteenth % (this.numBeatsPerBar * 4)) + 1 || 1;

        let res = `${bar}.${beat}`;
        if (this.gridDivision === 2) { // eight
            res += `.${eighth}`;
        }

        if (this.gridDivision === 4) { // sixteenth
            res += `.${sixteenth}`;
        }
        return res;
    };

    /**
     * Prepare and set the music message data to the project and chat containers
     * @param track {Object}    The selected track
     * @returns {{projectId, trackId, fileId: *|String, trackActionData: *, startTime: number, endTime: number}}
     */
    setMusicMessage(track, splitIndex) {
        if (!isEmpty(looperStore.loopedProject)) {
            const activeBars = this.getSelectedBeatsFromTrack(track, splitIndex);
            const audioTrack = Object.assign({}, track);
            audioTrack.trackActionData = actionStore.getByTrackId(track.id).reduce((actions, curAction) => {
                if (curAction.type !== TrackActionType.Highlight) {
                    actions.push(action);
                }
                return actions;
            }, []);
            this.setHighlightBars(audioTrack, activeBars);
            const startTime = Math.min(...activeBars) * this.beatLength;
            const endTime = ((Math.max(...activeBars) + 1) * this.beatLength);
            const duration = endTime - startTime;

            const currentTracks = looperStore.tracks.slice();
            let atchColor = '';

            for (let i = 0; i < currentTracks.length; i += 1) {
                if (currentTracks[i].id === track.id) {
                    atchColor = getColorByTier(6 - i);
                    break;
                }
            }

            chatStore.setAudioAttachment(looperStore.loopedProject.id, {
                startTime,
                endTime,
                duration,
                atchColor,
                id: audioTrack.id,
                fileId: audioTrack.fileId,
                bpm: audioTrack.bpm,
                originalBPM: audioTrack.originalBPM,
                name: audioTrack.name,
                trackActionData: audioTrack.trackActionData,
                playColor: audioTrack.playColor,
                originalDuration: duration
            }, looperStore.loopedProject.type);
        }
    }

    handleCreateNewStem = async (trackIndex, selectedSplit) => {
        const track = this.tracks[trackIndex];
        const selectedBars = this.getSelectedBeatsFromTrack(track, selectedSplit);
        if (!isEmpty(track) && !isEmpty(selectedBars)) {
            const looperTrack = looperStore.looperTracks.find(t => t.id === track.id);
            const absoluteSourceBars = selectedBars;
            const barLength = (60 / track.masterBpm) * (4 / this.timeSignatureMeter) / this.gridDivision;
            //  const barLength = this.getObjectBarLength(track);
            const startTime = absoluteSourceBars[0] * barLength;
            const endTime = (absoluteSourceBars[absoluteSourceBars.length - 1] + 1) * barLength; // we need the end of the bar
            let newTrack = await this.createNewStem(looperTrack, startTime, endTime);
            newTrack = Object.assign({}, track, newTrack, {downloadPct: track.downloadPct});
            newTrack.version = track.version + 1;
            newTrack.startOffset = 0;
            newTrack.copiedFrom = track.id;
            newTrack.saved = false;
            newTrack.trackActionData = null;
            await looperStore.addToLooper(newTrack, false);
        }
    };


    @computed
    get beatLength() {
        return (60 / this.bpm) * (4 / this.timeSignatureMeter);
    }

    @computed
    get tracks() {
        return this.studioTracks.slice();
    }

    @computed
    get looperTracks() {
        return this.studioTracks.filter(track => track.beatCount > 0);
    }

    @computed
    get leaderTrack() {
        return this.tracks
            .reduce((leader, track) => {
                return (isEmpty(leader) || leader.duration < track.duration) ? track : leader;
            }, {});
    }

    @computed
    get minGridSize() {
        let lastSplit = null;
        let end = null;
        this.looperTracks.forEach((track) => {
            const localLastSplit = last(track.splits);
            if (!lastSplit) {
                lastSplit = localLastSplit;
            }
            const localEnd = localLastSplit.offset + (localLastSplit.rightTrim || localLastSplit.end);
            end = lastSplit.offset + (lastSplit.rightTrim || lastSplit.end);
            if (localEnd > end) {
                end = localEnd;
                lastSplit = localLastSplit;
            }
        });
        return Math.round(end / this.gridDivision);
    }

    fixBeatCount(beatCount) {
        return (beatCount * this.timeSignatureMeter) / 4;
    }

    @computed
    get beatCount() {
        return looperStore.studioBeatsCount || 128;
    }

    @computed
    get absBeatCount() {
        return (looperStore.studioBeatsCount || 128) / this.gridDivision;
    }

    @computed
    get duration() {
        return this.absBeatCount * this.beatLength;
    }

    @computed
    get copiedBars() {
        return this.clipboard.copiedBars;
    }

    @computed
    get isCut() {
        return this.clipboard.isCut;
    }

    // @computed
    // get slice() {
    //     return toJS(this.playSlice);
    // }

    @computed
    get showCopyArc() {
        return this.clipboard.copiedBars.size > 0;
    }

    @computed
    get gridDivisionObject() {
        return gridDivisionArray.find(item => item.value === this.gridDivision);
    }


    getNumberOfBeats(track, ignoreSplits = false) {
        if (!ignoreSplits && !isEmpty(track.splits)) {
            const firstSplit = first(track.splits);
            const lastSplit = last(track.splits);
            return ((lastSplit.rightTrim || lastSplit.end + lastSplit.offset) - (firstSplit.leftTrim || firstSplit.start + firstSplit.offset)) / this.gridDivision;
        }
        return Math.ceil(Math.round((track.duration / (60 / this.bpm)) * 1e10) / 1e10);
    }

    /**
     * Calculate the active bar count according to the loopers
     */
    @action
    getStudioTrackDataFromLoopers(tracks, isLooperLoaded) {
        const leaderTrack = tracks
            .filter(track => track.bpm !== -1)
            .reduce((curLeaderTrack, track) => {
                if (isEmpty(curLeaderTrack) || curLeaderTrack.duration < track.duration) {
                    curLeaderTrack = track;
                }
                return curLeaderTrack;
            }, {});

        // this.leaderTrack = leaderTrack;

        let recalcGridDivision = false;
        if (isEmpty(tracks)) {
            this.isEditMode = false;
            this.clearPlaySlice(false);
            this._updateBeatCount(null);
        } else {
            // we should change the number of beats if:
            // the user has set a value - or if the current track is longer than the current value
            if (this.getNumberOfBeats(leaderTrack) * this.gridDivision > looperStore.studioBeatsCount && this.looperTracks.length < tracks.length) {
                recalcGridDivision = true;
            }
            looperStore.studioBeatsCount = this.getNumberOfBeats(leaderTrack, true) || 32;
            if (!looperStore.studioBeatsCount || (this.getNumberOfBeats(leaderTrack) > looperStore.studioBeatsCount && this.looperTracks.length < tracks.length)) {
                looperStore.studioBeatsCount = this.getNumberOfBeats(leaderTrack);
            }
            // }
        }

        this.setBpm(looperStore.bpm || 120);

        // looperStore.studioBeatsCount *= 4 / this.timeSignatureMeter;

        this.timeSignatureMeter = 4;
        this.numBeatsPerBar = 4;
        this.timeSignatureLabel = '4/4';

        this.studioTracks.replace(this.buildStudioTracks(tracks));

        if (looperStore.loopedProject.gridDivision) {
            this.gridDivision = looperStore.loopedProject.gridDivision;
            this.gridDivisionLabel = gridDivisionArray.filter(d => d.value === this.gridDivision)[0].label;
        } else if (this.userGridDivision === null || recalcGridDivision) {
            // the max grid division index is 2 ("1/4") and the min is 6 ("4")
            // for every MAX_NUM_BEATS we add an index, and normalize it by 2 (since up to 40 beats, we want "1/4" = 2)
            let gridIndex = Math.min(Math.max(Math.floor((looperStore.studioBeatsCount / MAX_NUM_BEATS)) + 2, 2), 6);
            if (!gridIndex) {
                gridIndex = 2; // '1/4'
            }
            const gridDivisionObject = gridDivisionArray[gridIndex];
            this.gridDivision = gridDivisionObject.value;
            this.gridDivisionLabel = gridDivisionObject.label;
            this.userGridDivision = null;
        } else {
            this.gridDivision = this.userGridDivision;
        }


        this.looperTracks.forEach((track) => {
            // track.beatCount *= this.gridDivision;
            track.splits.forEach((split) => {
                split.start *= this.gridDivision;
                split.end *= this.gridDivision;
                split.offset *= this.gridDivision;
                split.leftTrim *= this.gridDivision;
                split.rightTrim *= this.gridDivision;
            });
        });
        looperStore.studioBeatsCount *= this.gridDivision;

        // console.log('studio beat count: ' + looperStore.studioBeatsCount);

        this.applyTrackActionData(this.looperTracks);

        this.padTracks(this.studioTracks, this.duration); // make sure we add
        looperStore.setProjectDuration(this.duration);
        looperStore.setProjectTimeSignatureMeter(this.timeSignatureMeter);
        looperStore.setProjectNumBeatsPerBarMeter(this.numBeatsPerBar);


        let musicalKey = looperStore.loopedProject.musicalKey;
        let keyIndex = transpositionArray.findIndex(key => key === musicalKey);
        this.transposeArray.replace(pitchShiftArray.map(trans => {
            if (keyIndex >= 0) {
                let label = transpositionArray[(trans.value + keyIndex + transpositionArray.length) % transpositionArray.length];
                return {label: `${trans.value !== 0 ? (trans.label + ' ') : ''}(${label})`, value: trans.value};
            } else {
                return {label: `${trans.label}`, value: trans.value};
            }

        }));


        if (looperStore.studioBeatsCount) {
            looperStore.setProjectBeatCount(this.beatCount);
        }
        this.looperTracks.forEach(object => this.applyTrackActionDataToAudio(object, false));
        // setTimeout(async () => {
        //     await looperStore.updateMetronome();
        //     // looperStore.playPauseIfWasPlaying();
        // }, 0);

        this.clearAllSelectedItems(false);

        const slice = looperStore.loopedProject.slice;
        if (slice) {
            let trackIndex = slice.trackIndex;
            let splitIndex = slice.splitIndex;
            let selectedBeats = slice.selectedBeats;
            if (this.studioTracks[trackIndex] && this.studioTracks[trackIndex].splits) {
                if (isNumber(slice.start) && isNumber(slice.end)) {
                    looperStore.setPlaySlice(slice.start, slice.end, trackIndex, splitIndex, selectedBeats, false);
                } else {
                    this.studioTracks[trackIndex].splits[splitIndex].selectedBeats = selectedBeats;
                    this.togglePlaySlice(trackIndex, splitIndex, false);
                }
                this.isLoopActive = true;
            }
        }
    }

    @action
    initStudioTracks() {
        const studioTracks = [];
        for (let i = studioTracks.length; i < MAX_LOOPER_TRACKS; i += 1) {
            studioTracks.push({barCount: 0, progress: 0, duration: 0, currentTime: 0});
        }
        return studioTracks;
    }

    @action clearPlaySlice = (userAction = true) => {
        // this.playSlice = null;
        looperStore.setPlaySlice(null, null, null, null, null, userAction);
    };


    @action togglePlaySlice = (trackIndex, splitIndex, userAction = true) => {
        const track = this.tracks[trackIndex];
        if (!isEmpty(track) && track.splits) {
            const selectedBeats = this.getSelectedBeatsFromTrack(track, splitIndex);
            let playSlice = this.barsToTimeRange(selectedBeats, this.beatLength);
            looperStore.setPlaySlice(playSlice.start / this.gridDivision, playSlice.end / this.gridDivision, trackIndex, splitIndex, selectedBeats, userAction);
        }
    };

    /**
     * Return the start/end time of bar array
     */
    barsToTimeRange = (bars, barLength) => {
        return {start: Math.min(...bars) * barLength, end: (Math.max(...bars) + 1) * barLength};
    };

    /**
     * On change looper leader bpm update the bpm
     * @param bpm
     */
    @action
    setBpm(bpm) {
        this.bpm = bpm;
    }

    @action
    setPitch(value, label) {
        this.pitchLabel = label;
        looperStore.setProjectPitch(value);
    }


    /**
     * Set the new grid division
     * @param val {Object}    The grid division selected
     */
    @action
    setGridDivision(val, label) {
        const ratio = val / this.gridDivision;
        this.gridDivision = val;
        this.gridDivisionLabel = label;
        this.userGridDivision = this.gridDivision;
        this.looperTracks.forEach((track) => {
            track.splits.forEach((split) => {
                split.start *= ratio;
                split.end *= ratio;
                split.offset *= ratio;
                split.leftTrim *= ratio;
                split.rightTrim *= ratio;
                if (split.selectedBeats.length > 0) {
                    let l = split.selectedBeats.length;
                    let f = first(split.selectedBeats);
                    split.selectedBeats.replace(range(f * ratio, (f + l) * ratio));
                }
            });
        });
        looperStore.setProjectGridDivision(val);
        looperStore.studioBeatsCount *= ratio;
        // this.clearPlaySlice(false);
    }

    _updateBeatCount(beats) {
        looperStore.studioBeatsCount = beats;
        looperStore.setProjectDuration(this.duration);
        looperStore.setProjectBeatCount(beats);
    }

    _handleSplitAction(track, startBar, endBar, remove = false, copySourceStart = null) {
        const newSplits = [];
        let splitStart;
        let splitEnd;
        track.splits.forEach((split) => {
            const {start, end, leftTrim, rightTrim, offset, sourceStart} = split;
            splitStart = leftTrim !== 0 ? leftTrim + offset : start + offset;
            splitEnd = rightTrim !== 0 ? rightTrim + offset : end + offset;
            if (endBar < splitStart) {
                const newSplit = {...split};
                const newSplitStart = newSplit.start + offset < endBar ? endBar - offset : newSplit.start;
                newSplit.sourceStart = newSplit.sourceStart - (newSplit.start - newSplitStart);
                newSplit.start = newSplitStart;
                newSplits.push(newSplit);
            } else if (splitEnd <= startBar) {
                const newSplit = {...split};
                newSplit.end = newSplit.end + offset > startBar ? startBar - offset : newSplit.end;
                newSplits.push(newSplit);
            } else {
                if (startBar > splitStart) {
                    newSplits.push({
                        start,
                        end: startBar - offset,
                        selectedBeats: [],
                        offset,
                        leftTrim,
                        rightTrim: 0,
                        sourceStart
                    });
                }
                if (endBar < splitEnd) {
                    newSplits.push({
                        start: endBar - offset,
                        end,
                        selectedBeats: [],
                        offset,
                        leftTrim: 0,
                        rightTrim,
                        sourceStart: endBar - offset
                    });
                }
            }
        });
        if (!remove) {
            const sourceStart = isNumber(copySourceStart) ? copySourceStart : startBar;
            newSplits.push({start: startBar, end: endBar, selectedBeats: [], offset: 0, leftTrim: 0, rightTrim: 0, sourceStart});
        }
        track.splits.replace(newSplits.sort((a, b) => {
            return ((a.leftTrim || a.start) + a.offset) - ((b.leftTrim || b.start) + b.offset);
        }));
    }

    /**
     * Set the new beatCount
     * @param beats
     */
    @action
    setBeatCount(beats) {
        if (this.leaderTrack.beatCount > 0) {
            this._updateBeatCount(beats * this.gridDivision);
            this.padToDuration({id: 'global', bpm: this.bpm, tracks: this.looperTracks}, this.beatCount);
        }
    }


    @action
    clearStudioData(userAction) {
        this.isEditMode = false;
        this.clearPlaySlice(userAction);
        this.clipboard = this.initClipboard();
    }

    /**
     * Clear all selected tracks and bars
     */
    @action
    clearAllSelectedItems(userAction = true) {
        this.tracks.filter(track => !isEmpty(track.splits)).forEach((track) => {
            track.isSelectAll = false;
            track.splits.forEach((split) => {
                split.selectedBeats = [];
                split.selected = false;
            });
        });
        this.clearStudioData(userAction);
    }

    /**
     * Set the track start offset
     * @param track {Object}    The looper track object
     * @param splitIndex
     */
    @action setTrackStartOffset = (trackIndex, splitIndex) => {
        const track = this.tracks[trackIndex];
        if (!isEmpty(track)) {
            this.setStartOffset(track, splitIndex);
            // }
        }
    };

    /**
     * Set the track start offset
     * @param track {Object}    The looper track object
     * @param splitIndex
     */
    @action setTrackLeftTrim = (trackIndex, splitIndex) => {
        const track = this.tracks[trackIndex];
        if (!isEmpty(track)) {
            this.setLeftTrim(track, splitIndex);
        }
    };

    @action setTrackRightTrim = (trackIndex, splitIndex) => {
        const track = this.tracks[trackIndex];
        if (!isEmpty(track)) {
            this.setRightTrim(track, splitIndex);
        }
    };


    handlePasteAction = (selectedTrack, pasteToIndex, copiedBars, copySourceStart) => {
        const startBar = pasteToIndex;
        const endBar = startBar + (last(copiedBars) - first(copiedBars) + 1);
        const pasteToBarsRes = copiedBars.slice().map((b, i) => pasteToIndex + i);
        // if (endBar > this.beatCount) {
        //     return null;
        // }
        this._handleSplitAction(selectedTrack, startBar, endBar, false, copySourceStart);
        return pasteToBarsRes;
    };

    getSplitRange = (split) => {
        const start = (split.leftTrim || split.start) + split.offset;
        const end = (split.rightTrim || split.end) + split.offset;
        let res = range(start, end);

        if (end - Math.floor(end) > 0) {
            res[res.length - 1] = end - 1;
        }
        return res;
    };

    /**
     * Perform the pasteBars action
     * @param trackID   {String}    The selected track id
     */
    @action pasteBars = (trackIndex) => {
        const trackID = this.tracks[trackIndex].id;
        const copiedBars = this.clipboard.copiedBars.get(trackID).slice();
        const copyOffset = this.clipboard.copyOffset.get(trackID);
        if (!isEmpty(copiedBars)) {
            const selectedTrack = this.tracks.find(track => track.id === trackID);
            if (!isEmpty(selectedTrack)) {
                const pasteToBars = this.handlePasteAction(selectedTrack, this.clipboard.nextPasteIndex, copiedBars, first(copiedBars) - copyOffset);
                if (last(pasteToBars) + 1 > this.beatCount) {
                    this.setBeatCount((last(pasteToBars) + 1) / this.gridDivision);
                }
                if (pasteToBars) {
                    // find the split
                    const newSplitIndex = this.findSplitIndex(selectedTrack, first(pasteToBars), last(pasteToBars));
                    selectedTrack.splits[newSplitIndex].selectedBeats = pasteToBars;
                    this.paste(selectedTrack, pasteToBars, copiedBars, this.clipboard.isCut, newSplitIndex);

                    if (this.clipboard.isCut) {
                        this.cutBars(selectedTrack, copiedBars);
                    }
                    this.clipboard.nextPasteIndex = this.clipboard.nextPasteIndex + (last(copiedBars) - first(copiedBars) + 1);
                } else {
                    appStore.createToast({
                        message: <FormattedMessage
                            id="notifications.pasteExceed"
                            values={{studioBeatsCount: looperStore.studioBeatsCount}}/>,
                        duration: 3000,
                        className: 'left-offset-43'
                    });
                }
            }
        }
    };

    findSplitIndex = (track, firstBeat, lastBeat) => {
        return track.splits.findIndex((split) => {
            return split.start + split.offset <= firstBeat && lastBeat < split.end + split.offset;
        });
    };

    /**
     * Handle the cut bars action - fix the bar according to the offset
     * @param track {Object}    The track to update
     * @param source    {Array} The cut bars
     */
        // @action cutBars = (track, source) => {
    @action cutBars = () => {
        this.clipboard.isCut = false;
    };

    /**
     * Add the track data to our clipboard
     * @param track {Object}    The track to copy
     * @param splitIndex
     * @param isCut {Boolean}   Indicator whether or not we are in cut mode
     */
    @action setToClipboard = (trackIndex, splitIndex, isCut) => {
        const track = this.tracks[trackIndex];
        const activeBars = this.getSelectedBeatsFromTrack(track, splitIndex);
        this.setCopiedBars(track, activeBars, splitIndex);
        this.clipboard.copiedBars.set(track.id, activeBars);
        this.clipboard.copyOffset.set(track.id, track.splits[splitIndex].offset);
        this.clipboard.isCut = isCut;
        this.clipboard.splitIndex = splitIndex;
        this.clipboard.nextPasteIndex = activeBars[activeBars.length - 1] + 1;
    };

    setNextPasteIndex = (trackIndex, splitIndex) => {
        this.clipboard.nextPasteIndex = this.tracks[trackIndex].splits[splitIndex].selectedBeats[0];
    };

    /**
     * Add the track data to our clipboard
     * @param track {Object}    The track to copy
     * @param splitIndex
     */
    @action deleteSelected = (trackIndex, splitIndex) => {
        const track = this.tracks[trackIndex];
        const activeBars = this.getSelectedBeatsFromTrack(track, splitIndex);
        this._handleSplitAction(track, first(activeBars), last(activeBars) + 1, true);
        this.handleAddNewAction(track, TrackActionType.SplitRemove, [], activeBars, splitIndex);
        this.clearAllSelectedItems();
        this.isLoopActive = false;
        if (isEmpty(track.splits)) {
            looperStore.removeFromLooper(track);
        }
    };

    // /**
    //  * Set bar array as copied bars
    //  * @param track   {Object}    The track to copy
    //  * @param bars  {Array} The bars to copy
    //  * @param isCut {Boolean}   Indicator whether or not we are in copy mode or cut
    //  * @private
    //  */
    // @action _copyBarData(track, bars, isCut=false){
    //     let copyData = [];
    //     bars.forEach(bar=>copyData.push(track.barMap.get(bar)));
    //     this.clipboard.copiedData.set(track.id,copyData);
    // };

    _formatUndoRedoToast = (type, curAction) => {
        const values = {
            type,
            actionType: translate(`studio.actions.${TrackActionType(curAction.type)}`),
            actionSource: !isEmpty(curAction.source) ? getRangeString(curAction.source) : '',
            to: !isEmpty(curAction.source) ? translate('studio.actions.to') : '',
            actionTarget: getRangeString(curAction.target.filter(t => isNumber(t)))
        };
        appStore.createAlertFromMessageId(AlertType.Info, 'notifications.undoRedo', values, 3500);
    };
    /**
     * Perform undo to action
     */
    @action undoAction = () => {
        this.isEditMode = false; // disable edit mode, since selection is often wrong after undo
        // this._updateBeatCount(this.getBeatCount(Math.round(this.leaderTrack.beatCount)));
        const action = this.undo(this.looperTracks);
        if (action) {
            this._formatUndoRedoToast('Undo', action);
            this.getStudioTrackDataFromLoopers(looperStore.looperTracks, true);
            looperStore.playPauseIfWasPlaying();
        }
        // this.applyTrackActionData(tracks);
        // this._handleGenericActions(action);
        // this.padTracks(tracks, this.duration);
        // this.getStudioTrackDataFromLoopers(tracks, looperStore.isLooperLoaded);
    };
    /**
     * Perform redo action
     */
    @action redoAction = () => {
        this.isEditMode = false; // disable edit mode, since selection is often wrong after undo
        const action = this.redo(this.looperTracks);
        if (action) {
            this._formatUndoRedoToast('Redo', action);
            this.getStudioTrackDataFromLoopers(looperStore.looperTracks, true);
            looperStore.playPauseIfWasPlaying();
        }
    };


    /**
     * Handle setting new actions
     * @param object {Object}    The object to modify
     * @param type  {Number}    The action type
     * @param source    {Array} The source data for the action
     * @param target    {Array} The target data for the action
     * @param splitIndex
     * @param isGlobal    {Boolean}   Indicator whether or not to save action to the object or global level
     */
    @action
    handleAddNewAction(object, type, source, target, splitIndex, isGlobal = false, applyActionToAudio = true) {
        const action = new TrackAction(type, source, target, object, this.getObjectBarLength(object), splitIndex, isGlobal, this.gridDivision);
        actionStore.add(action);
        looperStore.setIsEditedProject(true);
        // {trackId: object.id, actionId: action.id, action: action}
        if (!isGlobal) {
            object.trackActionData.push(action);
            if (applyActionToAudio) {
                this.applyTrackActionDataToAudio(object, true);
                looperStore.playPauseIfWasPlaying();
            }
        }
    }


    /**
     * Set the track start offset
     * @param object {Object}    The object to modify
     * @param splitIndex
     */
    @action setStartOffset = (object, splitIndex) => {
        if (!isEmpty(object)) {
            const split = object.splits[splitIndex];
            this.handleAddNewAction(object, TrackActionType.StartOffset, range(split.leftTrim || split.start, split.rightTrim || split.end), [split.offset], splitIndex);
        }
    };

    /**
     * Set the track start offset
     * @param object {Object}    The object to modify
     * @param splitIndex
     */
    @action setLeftTrim = (object, splitIndex) => {
        if (!isEmpty(object)) {
            const split = object.splits[splitIndex];
            this.handleAddNewAction(object, TrackActionType.LeftTrimOffset, [], [split.leftTrim], splitIndex);
        }
    };

    /**
     * Set the track start offset
     * @param object {Object}    The object to modify
     * @param splitIndex
     */
    @action setRightTrim = (object, splitIndex) => {
        if (!isEmpty(object)) {
            const split = object.splits[splitIndex];
            this.handleAddNewAction(object, TrackActionType.RightTrimOffset, [], [split.rightTrim], splitIndex);
        }
    };

    /**
     * Add padding to the objects after adding bars
     * @param project    {Object} The project object
     * @param target    {Number}    The bar to pad to
     */
    @action padToDuration = (project, target) => {
        this.handleAddNewAction(project, TrackActionType.Padding, [], [target], null, true);
        this.padTracks(project.tracks, this.duration);
        project.tracks.forEach(object => this.applyTrackActionDataToAudio(object, false));
        setTimeout(async () => {
            await looperStore.updateMetronome();
            looperStore.playPauseIfWasPlaying();
        }, 0);
    };
    /**
     * Set the track highlight bars
     * @param object {Object}    The object to modify
     * @param target    {Array}    The bars to highlight
     */
    @action setHighlightBars = (object, target) => {
        if (!isEmpty(object)) {
            this.handleAddNewAction(object, TrackActionType.Highlight, [], target);
        }
    };

    /**
     * Set the track copy bars
     * @param object {Object}    The object to modify
     * @param target    {Array}    The bars to copy
     * @param splitIndex
     */
    @action setCopiedBars = (object, target, splitIndex) => {
        if (!isEmpty(object)) {
            this.handleAddNewAction(object, TrackActionType.Copy, [], target, splitIndex);
        }
    };

    /**
     *Perform thew mute un-mute functionality
     * @param object {Object}    The object to modify
     * @param indexes {Array}    The bar indexes to mute/un-mute
     * @param curAction    {Number}   the action to perform (mute or un-mute)
     * @param splitIndex
     */
    @action mute = (object, indexes, curAction, splitIndex) => {
        if (!isEmpty(object) && !isEmpty(indexes)) {
            this.handleAddNewAction(object, curAction, [], indexes, splitIndex);
        }
    };

    /**
     *Perform the pasteBars functionality
     * @param object {Object}    The object to modify
     * @param pasteTo {Array}    The destination of the pasteBars (bars to override)
     * @param copiedBars    {Array}   The bar data to be pasted
     * @param isCut {Boolean}    Indicator whether or not we are in cut mode
     * @param splitIndex
     */
    @action paste = (object, pasteTo, copiedBars, isCut, splitIndex) => {
        if (!isEmpty(object) && !isEmpty(copiedBars)) {
            this.handleAddNewAction(object, TrackActionType.Split, [], pasteTo, splitIndex, false, false);
            this.handleAddNewAction(object, !isCut ? TrackActionType.Paste : TrackActionType.Cut, copiedBars, pasteTo, splitIndex);
        }
    };

    @action handleSkip = (status, seekTo, source = 'button') => {
        looperStore.updateTrackCurrentTimeAndProgress(this.leaderTrack.id);
        const activeBar = Math.round(this.leaderTrack.currentTime / (this.beatLength / this.gridDivision));
        let seekToTime;
        switch (status) {
            case 'next':
                customEvent(events.CustomEvent, {
                    category: category.Studio,
                    action: actions.MoveBarForward,
                    label: source
                });
                seekToTime = ((activeBar + 1) % this.beatCount) * this.beatLength / this.gridDivision;
                if (activeBar + 1 > this.beatCount) {
                    seekToTime = 0;
                }

                looperStore.looperTracksSeekTo(seekToTime);
                break;
            case 'previous':
                customEvent(events.CustomEvent, {
                    category: category.Studio,
                    action: actions.MoveBarBackward,
                    label: source
                });
                seekToTime = (((activeBar - 1) + this.beatCount) % this.beatCount) * this.beatLength / this.gridDivision;
                if (activeBar - 1 < 0) {
                    seekToTime = (Math.floor(this.beatCount)) * this.beatLength / this.gridDivision;
                }
                looperStore.looperTracksSeekTo(seekToTime);
                break;
            case 'seek':
                if (isNumber(seekTo)) {
                    looperStore.looperTracksSeekTo(seekTo * this.beatLength);
                }
                break;
            default:
                looperStore.looperTracksSeekTo(0);
                break;
        }
    };

    @action clearAllOtherSplitSelections = (tier, splitIndex) => {
        this.isEditMode = true;
        this.tracks.forEach((track, i) => {
            if (track.splits && track.splits.length > 0) {
                if (i !== tier) {
                    track.splits.forEach((split) => {
                        split.selectedBeats = [];
                        split.selected = false;
                    });
                } else {
                    track.splits.filter((split, index) => index !== splitIndex).forEach((split) => {
                        split.selectedBeats = [];
                        split.selected = false;
                    });
                    track.splits[splitIndex].selected = true;
                    // track.splits[splitIndex].selectedBeats = [];
                }
            }
        });
    }

    @action setTrackColor = (trackIndex, color) => {
        this.tracks[trackIndex].color = color;
    }

    @action toggleIsShowWaves = () => {
        customEvent(events.CustomEvent, {category: isGuest() ? category.Discover : category.Studio, action: actions.ToggleWaveform, label: 'Audio'});
        this.isShowWaves = !this.isShowWaves;
    };

    @computed get advancedMode() {
        console.log(looperStore.advancedMode);
        return looperStore.advancedMode;
    };


    @action toggleLoopState = () => {
        this.isLoopActive = !this.isLoopActive;
    }
}

const studioStore = new StudioStore();
export default studioStore;

